<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAnimalStore } from '@/stores/animal'
import { useAuthStore } from '@/stores/auth'
import { animalService } from '@/api/animal'
import AnimalForm from '@/components/AnimalForm.vue'
import HealthManagement from '@/components/health/HealthManagement.vue'

interface Animal {
  id?: string
  species: string
  breed?: string
  gender: string
  health_status: string
  location: string
  count: number
  owner_name?: string
  created_by_name?: string
  neutered_date?: string | null
  acquisition_date: string
  acquisition_method?: string
  symptoms?: string
  owner?: string
  created_by?: string
  updatedAt?: string
}

const animalStore = useAnimalStore()
const authStore = useAuthStore()

const animals = computed<Animal[]>(() => {
  const list = (animalStore.animals ?? []) as unknown
  if (!Array.isArray(list)) return []
  return list.filter((a): a is Animal => a != null)
})

const loading = ref(false)
const showRegisterModal = ref(false)
const showHealthManagement = ref(false)
const selectedAnimal = ref<Animal | null>(null)
const editingAnimal = ref<string | null>(null)
const editForm = ref<Animal | null>(null)
const searchQuery = ref(animalStore.searchQuery)
const selectedLimit = ref(animalStore.pageSize)

onMounted(async () => {
  await fetchAnimals()
})

async function fetchAnimals(page = animalStore.currentPage, limit = selectedLimit.value, search = searchQuery.value) {
  loading.value = true
  try {
    await animalStore.fetchAnimals({ page, limit, search })
  } catch (err) {
    console.error('Failed to fetch animals:', err)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  fetchAnimals(1) // Reset to page 1 on search
}

function handleLimitChange() {
  fetchAnimals(1) // Reset to page 1 on limit change
}

const totalCount = computed(() => animalStore.totalCount)
const currentPage = computed(() => animalStore.currentPage)
const pageSize = computed(() => animalStore.pageSize)
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))

function handlePageChange(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    fetchAnimals(page)
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    handlePageChange(currentPage.value + 1)
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    handlePageChange(currentPage.value - 1)
  }
}

function openRegisterModal() {
  showRegisterModal.value = true
}

function closeRegisterModal() {
  showRegisterModal.value = false
}

function openHealthManagement(animal: Animal) {
  selectedAnimal.value = animal
  showHealthManagement.value = true
}

function closeHealthManagement() {
  showHealthManagement.value = false
  selectedAnimal.value = null
}

function startEdit(animal: Animal) {
  if (!animal.id) return
  editingAnimal.value = animal.id
  editForm.value = { ...animal }
}

function cancelEdit() {
  editingAnimal.value = null
  editForm.value = null
}

async function saveEdit(animalId: string) {
  if (!editForm.value) return

  const payload: Partial<Animal> = {
    species: editForm.value.species,
    breed: editForm.value.breed,
    gender: editForm.value.gender,
    neutered_date: editForm.value.gender === 'Neutered' ? editForm.value.neutered_date || null : null,
    acquisition_date: editForm.value.acquisition_date,
    acquisition_method: editForm.value.acquisition_method,
    health_status: editForm.value.health_status,
    symptoms: editForm.value.health_status === 'Sick' ? editForm.value.symptoms || '' : '',
    location: editForm.value.location,
    count: editForm.value.count,
  }

  try {
    await animalService.updateAnimal(animalId, payload as any)
    editingAnimal.value = null
    editForm.value = null
    await fetchAnimals() // Refresh the list
  } catch (err) {
    console.error('Failed to update animal:', err)
  }
}

async function handleDelete(animalId?: string) {
  if (!animalId) return
  if (confirm('Are you sure you want to delete this animal?')) {
    try {
      await animalService.deleteAnimal(animalId)
      await fetchAnimals() // Refresh the list
    } catch (err) {
      console.error('Failed to delete animal:', err)
    }
  }
}

function canModify(animal: Animal): boolean {
  return authStore.user?.id === animal.owner || authStore.user?.id === animal.created_by
}

function getHealthStatusColor(status: string) {
  const colors = {
    'Healthy': 'bg-green-100 text-green-800',
    'Sick': 'bg-red-100 text-red-800',
    'Injured': 'bg-orange-100 text-orange-800',
    'Quarantined': 'bg-yellow-100 text-yellow-800',
    'Deceased': 'bg-gray-100 text-gray-800'
  }
  return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
}

function handleAnimalRegistered() {
  closeRegisterModal()
  fetchAnimals() // Refresh the list when a new animal is registered
}
</script>

<template>
  <div class="min-h-screen bg-gray-50/50 p-6">
    <div class="max-w-7xl mx-auto space-y-8">
      <!-- Header -->
      <div class="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl shadow-lg p-8 text-white relative overflow-hidden">
        <div class="absolute inset-0 bg-white/10 opacity-20 transform -skew-x-12 translate-x-1/2"></div>
        <div class="relative z-10">
          <h1 class="text-3xl font-extrabold tracking-tight">Livestock Management</h1>
          <p class="mt-2 text-emerald-50 text-lg max-w-2xl">
            Monitor, track, and manage your farm's animal inventory with ease.
          </p>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <!-- Table Header with Register Button -->
        <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div class="flex items-center space-x-2">
            <h2 class="text-lg font-semibold text-gray-800">Registered Animals</h2>
            <span class="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
              {{ totalCount }} Total
            </span>
          </div>
          <div class="flex space-x-3">
            <button
              @click="fetchAnimals(currentPage)"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <svg
                class="w-4 h-4 mr-2"
                :class="{ 'animate-spin': loading }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                ></path>
              </svg>
              {{ loading ? 'Refreshing...' : 'Refresh' }}
            </button>
            <button
              @click="openRegisterModal"
              class="inline-flex items-center px-4 py-2 bg-emerald-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors duration-200 shadow-sm"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Register New Animal
            </button>
          </div>
        </div>

        <!-- Search and Filter Bar -->
        <div class="px-6 py-4 border-b border-gray-100 bg-white grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="relative md:col-span-2">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              v-model="searchQuery"
              @input="handleSearch"
              type="text"
              placeholder="Search by species or breed/variety..."
              class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm transition-all"
            />
          </div>
          <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-600 whitespace-nowrap">Show:</label>
            <select
              v-model="selectedLimit"
              @change="handleLimitChange"
              class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-lg"
            >
              <option :value="12">12 per page</option>
              <option :value="24">24 per page</option>
              <option :value="48">48 per page</option>
              <option :value="100">100 per page</option>
            </select>
          </div>
        </div>

        <!-- Loading / Empty states -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-12">
          <div class="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4"></div>
          <p class="text-gray-500 font-medium">Loading animals...</p>
        </div>
        
        <div v-else-if="animals.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
          <div class="bg-gray-100 p-4 rounded-full mb-4">
            <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-1">No animals registered yet</h3>
          <p class="text-gray-500 max-w-sm mb-4">Get started by registering your first animal.</p>
          <button
            @click="openRegisterModal"
            class="inline-flex items-center px-4 py-2 bg-emerald-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors duration-200"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Register Your First Animal
          </button>
        </div>

        <!-- Animals Table -->
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Species & Breed</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Health Status</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Acquisition</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ownership</th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="animal in animals" :key="animal.id" class="hover:bg-gray-50 transition-colors duration-150">
                <!-- Species & Breed -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-lg">
                      {{ animal.species.charAt(0) }}
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">{{ animal.species }}</div>
                      <div class="text-sm text-gray-500">{{ animal.breed || 'Unknown Breed' }}</div>
                    </div>
                  </div>
                </td>
                
                <!-- Details -->
                <td class="px-6 py-4 whitespace-nowrap">
                   <div class="space-y-1">
                     <div class="text-sm text-gray-900 flex items-center">
                       <span class="font-medium mr-2">Gender:</span> 
                       <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                             :class="animal.gender === 'Female' ? 'bg-pink-100 text-pink-800' : animal.gender === 'Male' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'">
                         {{ animal.gender }}
                       </span>
                     </div>
                     <div class="text-sm text-gray-500">Count: {{ animal.count }}</div>
                     <div v-if="animal.neutered_date" class="text-xs text-orange-600">
                        Neutered: {{ animal.neutered_date }}
                     </div>
                   </div>
                </td>

                <!-- Health Status -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border"
                    :class="{
                      'bg-green-100 text-green-800 border-green-200': animal.health_status === 'Healthy',
                      'bg-red-100 text-red-800 border-red-200': animal.health_status === 'Sick',
                      'bg-yellow-100 text-yellow-800 border-yellow-200': animal.health_status === 'Injured',
                      'bg-purple-100 text-purple-800 border-purple-200': animal.health_status === 'Quarantined'
                    }">
                    <span class="w-1.5 h-1.5 rounded-full mr-1.5"
                      :class="{
                        'bg-green-400': animal.health_status === 'Healthy',
                        'bg-red-400': animal.health_status === 'Sick',
                        'bg-yellow-400': animal.health_status === 'Injured',
                        'bg-purple-400': animal.health_status === 'Quarantined'
                      }"></span>
                    {{ animal.health_status }}
                  </span>
                  <div v-if="animal.symptoms" class="text-xs text-red-500 mt-1 max-w-[150px] truncate" :title="animal.symptoms">
                    Sx: {{ animal.symptoms }}
                  </div>
                </td>

                <!-- Location -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div class="flex items-center">
                    <svg class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    {{ animal.location }}
                  </div>
                </td>

                <!-- Acquisition -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>{{ new Date(animal.acquisition_date).toLocaleDateString() }}</div>
                  <div v-if="animal.acquisition_method" class="text-xs text-gray-400 mt-0.5 capitalize">{{ animal.acquisition_method }}</div>
                </td>

                <!-- Ownership -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div><span class="text-xs text-gray-400 block">Owner:</span> {{ animal.owner_name }}</div>
                  <div><span class="text-xs text-gray-400 block mt-1">Cr. By:</span> {{ animal.created_by_name || '-' }}</div>
                </td>

                <!-- Actions -->
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex justify-end space-x-3">
                    <button
                      @click="openHealthManagement(animal)"
                      class="text-blue-600 hover:text-blue-900 transition-colors"
                      title="Health Management"
                    >
                      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                      </svg>
                    </button>
                    <button
                      v-if="canModify(animal)"
                      @click="startEdit(animal)"
                      class="text-indigo-600 hover:text-indigo-900 transition-colors"
                      title="Edit"
                    >
                      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </button>
                    <button
                      v-if="canModify(animal)"
                      @click="handleDelete(animal.id)"
                      class="text-red-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination Controls -->
        <div v-if="totalPages > 1" class="px-6 py-4 border-t border-gray-100 bg-gray-50/30 flex items-center justify-between">
          <div class="flex-1 flex justify-between sm:hidden">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
          <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p class="text-sm text-gray-700">
                <template v-if="totalCount > 0">
                  Showing
                  <span class="font-medium">{{ (currentPage - 1) * pageSize + 1 }}</span>
                  to
                  <span class="font-medium">{{ Math.min(currentPage * pageSize, totalCount) }}</span>
                  of
                  <span class="font-medium">{{ totalCount }}</span>
                  results
                </template>
                <template v-else>
                  No results found
                </template>
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  @click="prevPage"
                  :disabled="currentPage === 1"
                  class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span class="sr-only">Previous</span>
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <template v-for="page in totalPages" :key="page">
                  <button
                    v-if="page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)"
                    @click="handlePageChange(page)"
                    :aria-current="currentPage === page ? 'page' : undefined"
                    class="relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors"
                    :class="currentPage === page 
                      ? 'z-10 bg-emerald-50 border-emerald-500 text-emerald-600' 
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'"
                  >
                    {{ page }}
                  </button>
                  <span
                    v-else-if="(page === 2 && currentPage > 3) || (page === totalPages - 1 && currentPage < totalPages - 2)"
                    class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                  >
                    ...
                  </span>
                </template>

                <button
                  @click="nextPage"
                  :disabled="currentPage === totalPages"
                  class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span class="sr-only">Next</span>
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Register Animal Modal -->
    <div
      v-if="showRegisterModal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeRegisterModal"></div>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white" id="modal-title">Register New Animal</h3>
            <button
              @click="closeRegisterModal"
              class="text-white hover:text-emerald-200 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div class="px-6 py-6 max-h-[70vh] overflow-y-auto">
            <AnimalForm @success="handleAnimalRegistered" />
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal (same as before) -->
    <div
      v-if="editingAnimal && editForm"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="cancelEdit"></div>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
            <h3 class="text-lg leading-6 font-bold text-white" id="modal-title">Edit Animal Details</h3>
          </div>
          
          <div class="px-6 py-6 max-h-[70vh] overflow-y-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Species -->
              <div class="col-span-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Species</label>
                <select v-model="editForm.species" class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm">
                  <option value="Cattle">Cattle</option>
                  <option value="Sheep">Sheep</option>
                  <option value="Goat">Goat</option>
                  <option value="Poultry">Poultry</option>
                  <option value="Swine">Swine</option>
                  <option value="Pigs">Pigs</option>
                  <option value="Horses">Horses</option>
                  <option value="Dogs">Dogs</option>
                   <option value="Cats">Cats</option>
                    <option value="Rabbits">Rabbits</option>
                     <option value="Chickens">Chickens</option>
                      <option value="Pegions">Pegions</option>
                </select>
              </div>

              <!-- Breed -->
              <div class="col-span-1">
                 <label class="block text-sm font-medium text-gray-700 mb-1">Breed</label>
                 <input
                   v-model="editForm.breed"
                   type="text"
                   class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                 />
              </div>

              <!-- Gender -->
              <div class="col-span-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select v-model="editForm.gender" class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm">
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Neutered">Neutered</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <!-- Neutered Date (Conditional) -->
              <div v-if="editForm.gender === 'Neutered'" class="col-span-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Neutered Date</label>
                <input
                  v-model="editForm.neutered_date"
                  type="date"
                  class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                />
              </div>
              <div v-else class="col-span-1"></div> <!-- Spacer -->

              <!-- Health Status -->
              <div class="col-span-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Health Status</label>
                <select v-model="editForm.health_status" class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm">
                  <option value="Healthy">Healthy</option>
                  <option value="Sick">Sick</option>
                  <option value="Injured">Injured</option>
                  <option value="Quarantined">Quarantined</option>
                </select>
              </div>

              <!-- Location -->
              <div class="col-span-1">
                 <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
                 <input
                   v-model="editForm.location"
                   type="text"
                   class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                 />
              </div>

              <!-- Symptoms (Conditional) -->
              <div v-if="editForm.health_status === 'Sick'" class="col-span-2">
                 <label class="block text-sm font-medium text-red-700 mb-1">Symptoms Description</label>
                 <textarea
                   v-model="editForm.symptoms"
                   rows="2"
                   class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                   placeholder="Describe symptoms..."
                 ></textarea>
              </div>

              <!-- Count -->
              <div class="col-span-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Count</label>
                <input
                  v-model.number="editForm.count"
                  type="number"
                  min="1"
                  class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                />
              </div>

              <!-- Acquisition Date -->
              <div class="col-span-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Acquisition Date</label>
                <input
                  v-model="editForm.acquisition_date"
                  type="date"
                  class="block w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          <div class="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              @click="saveEdit(editingAnimal)"
              class="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
            >
              Save Changes
            </button>
            <button
              type="button"
              @click="cancelEdit"
              class="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Health Management Modal -->
    <div
      v-if="showHealthManagement && selectedAnimal"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" aria-hidden="true" @click="closeHealthManagement"></div>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-7xl">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-bold text-white">
              Health Management - {{ selectedAnimal.species }} ({{ selectedAnimal.breed || 'Unknown breed' }})
            </h3>
            <button @click="closeHealthManagement" class="text-white hover:text-blue-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div class="px-6 py-6 max-h-[80vh] overflow-y-auto">
            <HealthManagement v-if="selectedAnimal?.id" :animal="selectedAnimal!" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
